import { gridToLatLon, haversine } from './gridLocator.js';
import { adjustHour, tzLabel } from './timezone.js';

// ── HF Propagation Knowledge ──────────────────────────────────
var BAND_PROPAGATION = {
  "160m": { type: "night",  dayWeight: 0.1, nightWeight: 1.0 },
  "80m":  { type: "night",  dayWeight: 0.2, nightWeight: 1.0 },
  "60m":  { type: "both",   dayWeight: 0.5, nightWeight: 0.8 },
  "40m":  { type: "both",   dayWeight: 0.6, nightWeight: 1.0 },
  "30m":  { type: "both",   dayWeight: 0.8, nightWeight: 0.7 },
  "20m":  { type: "both",   dayWeight: 1.0, nightWeight: 0.4 },
  "17m":  { type: "day",    dayWeight: 1.0, nightWeight: 0.1 },
  "15m":  { type: "day",    dayWeight: 1.0, nightWeight: 0.05 },
  "12m":  { type: "day",    dayWeight: 1.0, nightWeight: 0.02 },
  "10m":  { type: "day",    dayWeight: 1.0, nightWeight: 0.01 },
  "6m":   { type: "day",    dayWeight: 1.0, nightWeight: 0.05 },
  "2m":   { type: "local",  dayWeight: 0.5, nightWeight: 0.5 },
  "70cm": { type: "local",  dayWeight: 0.5, nightWeight: 0.5 }
};

var MIN_QSOS_FOR_RECOMMENDATION = 5;
var MIN_QSOS_FOR_HIGH_CONFIDENCE = 20;

// ── Helper: confidence level based on sample size ─────────────
function confidence(n) {
  if (n < MIN_QSOS_FOR_RECOMMENDATION) return "insuficiente";
  if (n < MIN_QSOS_FOR_HIGH_CONFIDENCE) return "baixa";
  if (n < 50) return "media";
  return "alta";
}

// ── Helper: check if band is propagation-consistent ───────────
function isPropagationConsistent(band, period) {
  var prop = BAND_PROPAGATION[band];
  if (!prop) return true; // unknown band, assume ok
  if (period === "day") return prop.dayWeight >= 0.3;
  if (period === "night") return prop.nightWeight >= 0.3;
  return true;
}

// ── Helper: get active hours from QSOs ────────────────────────
// Returns a Set of hours (0-23) where the operator actually had QSOs
function getActiveHours(qsos, utcOffset) {
  var hours = new Set();
  for (var i = 0; i < qsos.length; i++) {
    var hRaw = qsos[i].time ? parseInt(qsos[i].time.substring(0, 2)) : -1;
    if (hRaw >= 0) hours.add(adjustHour(hRaw, utcOffset));
  }
  return hours;
}

// ════════════════════════════════════════════════════════════════
// computeStats — core statistics (unchanged logic, improved day/night)
// ════════════════════════════════════════════════════════════════
// Max/min por laco: Function.prototype.apply nao aceita arrays enormes como
// lista de argumentos, e aqui os arrays crescem junto com o log do usuario.
function maxOf(arr) { var m = -Infinity; for (var i = 0; i < arr.length; i++) { if (arr[i] > m) m = arr[i]; } return m; }
function minOf(arr) { var m = Infinity; for (var i = 0; i < arr.length; i++) { if (arr[i] < m) m = arr[i]; } return m; }

export function computeStats(qsos, myGrid, utcOffset) {
  if (utcOffset === undefined || utcOffset === null) utcOffset = 0;
  var tzStr = tzLabel(utcOffset);
  var mc = gridToLatLon(myGrid || "GG54mc");
  if (!mc) mc = { lat: -25.43, lon: -49.27 };
  // Normalize modes: PH->SSB, RY->RTTY, DG->FT8, LSB/USB->SSB
    var MODE_NORM = {'PH':'SSB','RY':'RTTY','DG':'FT8','LSB':'SSB','USB':'SSB'};
    for (var ni = 0; ni < qsos.length; ni++) {
      if (MODE_NORM[qsos[ni].mode]) qsos[ni].mode = MODE_NORM[qsos[ni].mode];
    }
    var dists = [];
  for (var i = 0; i < qsos.length; i++) {
    var q = qsos[i];
    if (q.lat != null && q.lon != null) {
      q.distance = haversine(mc.lat, mc.lon, q.lat, q.lon);
      dists.push(q.distance);
    }
  }
  var byBand = {}, byMode = {}, byCountry = {}, byCont = {};
  var daysPerHour = {}; // track unique operating dates per hour for averaging
  var byHour = {}, byDate = {}, byCQ = {};
  var calls = new Set(), grids = new Set(), countries = new Set();
  for (var j = 0; j < qsos.length; j++) {
    var q2 = qsos[j];
    var bk = q2.band || "?"; byBand[bk] = (byBand[bk] || 0) + 1;
    var mk = q2.mode || "?"; byMode[mk] = (byMode[mk] || 0) + 1;
    byCountry[q2.entity] = (byCountry[q2.entity] || 0) + 1;
    byCont[q2.continent] = (byCont[q2.continent] || 0) + 1;
    var rawH = q2.time ? parseInt(q2.time.substring(0, 2)) : -1;
    var hk = rawH >= 0 ? String(adjustHour(rawH, utcOffset)).padStart(2, "0") : "??";
    byHour[hk] = (byHour[hk] || 0) + 1;
    var dk = q2.date || "?"; byDate[dk] = (byDate[dk] || 0) + 1;
    // Track unique dates per hour for averaging
    if (hk !== "??" && dk !== "?") {
      if (!daysPerHour[hk]) daysPerHour[hk] = new Set();
      daysPerHour[hk].add(dk);
    }
    var ck = q2.cq || 0; byCQ[ck] = (byCQ[ck] || 0) + 1;
    calls.add(q2.call);
    if (q2.grid) grids.add(q2.grid.substring(0, 4));
    countries.add(q2.entity);
  }

  // ── DXCC Matrix, WAC, WAZ ─────────────────────────────────
  var dxccData = {}; // entity → { bands: {band→{w:bool,c:bool}}, modes: {mode→{w:bool,c:bool}}, worked:bool, confirmed:bool, cont, cq }
  var wacData = {}; // continent → { worked:bool, confirmed:bool }
  var wazData = {}; // cqZone → { worked:bool, confirmed:bool, count:int }
  // Se algum QSO já traz o lotwDxcc oficial, confiamos no LoTW (não inventamos entidade por
  // prefixo para linhas confirmadas sem DXCC, p.ex. marítimo/aeronáutico móvel).
  var hasLotwDxcc = false;
  for (var lz = 0; lz < qsos.length; lz++) { if (qsos[lz].lotwDxcc) { hasLotwDxcc = true; break; } }
  for (var di = 0; di < qsos.length; di++) {
    var dq = qsos[di];
    var ent = dq.entity || "Unknown";
    var dband = dq.band || "?";
    var dmode = dq.mode || "?";
    var dconf = dq.lotwQsl || dq.qrzQsl || dq.comment === "LoTW" || dq.comment === "QRZ-C";
    // Para os prêmios DXCC (entidades, Honor Roll, Challenge) só vale confirmação LoTW —
    // é a regra do ARRL DXCC (QRZ não conta). WAC/WAZ (prêmios CQ) seguem com dconf.
    // Além disso, a confirmação DXCC segue exatamente a mesma regra do dxccLotwConfirmedCount:
    // tem DXCC oficial do LoTW, OU (sem backfill) não é /MM,/AM. Assim a matriz/Honor Roll
    // somam igual ao número de cabeçalho (= LoTW).
    var dcallU = (dq.call || "").toUpperCase();
    var dlotw = (dq.lotwQsl || dq.comment === "LoTW") &&
      (dq.lotwDxcc ? true : (!hasLotwDxcc && !/\/(MM|AM)$/.test(dcallU)));
    var dcont = dq.continent || "??";
    var dcq = dq.cq || 0;
    // DXCC entity tracking — confirmação = LoTW apenas
    if (ent !== "Unknown") {
      if (!dxccData[ent]) dxccData[ent] = { bands: {}, modes: {}, worked: true, confirmed: false, cont: dcont, cq: dcq };
      dxccData[ent].worked = true;
      if (dlotw) dxccData[ent].confirmed = true;
      if (dband !== "?") {
        if (!dxccData[ent].bands[dband]) dxccData[ent].bands[dband] = { w: true, c: false };
        dxccData[ent].bands[dband].w = true;
        if (dlotw) dxccData[ent].bands[dband].c = true;
      }
      if (dmode !== "?") {
        if (!dxccData[ent].modes[dmode]) dxccData[ent].modes[dmode] = { w: true, c: false };
        dxccData[ent].modes[dmode].w = true;
        if (dlotw) dxccData[ent].modes[dmode].c = true;
      }
    }
    // WAC tracking
    if (dcont !== "??" && dcont !== "AN") {
      if (!wacData[dcont]) wacData[dcont] = { worked: false, confirmed: false };
      wacData[dcont].worked = true;
      if (dconf) wacData[dcont].confirmed = true;
    }
    // WAZ tracking
    if (dcq > 0 && dcq <= 40) {
      if (!wazData[dcq]) wazData[dcq] = { worked: false, confirmed: false, count: 0 };
      wazData[dcq].worked = true;
      wazData[dcq].count++;
      if (dconf) wazData[dcq].confirmed = true;
    }
  }
  var dxccWorkedCount = Object.keys(dxccData).length;
  // DXCC LoTW-only confirmed: para o prêmio DXCC só contam entidades confirmadas pelo LoTW.
  // Prefere o número oficial da entidade (lotwDxcc) vindo do LoTW; cai no prefixo se ausente.
  var lotwDxccKeys = {};
  for (var li = 0; li < qsos.length; li++) {
    var lq = qsos[li];
    var lConf = lq.lotwQsl || lq.comment === "LoTW";
    if (!lConf) continue;
    if (lq.lotwDxcc) { lotwDxccKeys["#" + lq.lotwDxcc] = true; continue; }
    // Confirmado no LoTW mas sem DXCC: se o usuário já tem o backfill do LoTW, confie no
    // LoTW — DXCC ausente significa "sem entidade" (marítimo/aeronáutico móvel, sem match),
    // então não inventa entidade por prefixo. Sem backfill, aproxima por prefixo.
    if (hasLotwDxcc) continue;
    var lcall = (lq.call || "").toUpperCase();
    if (/\/(MM|AM)$/.test(lcall)) continue; // /MM e /AM não têm entidade DXCC
    var le = lq.entity || "Unknown"; if (le !== "Unknown") lotwDxccKeys[le] = true;
  }
  var dxccLotwConfirmedCount = Object.keys(lotwDxccKeys).length;
  // DXCC confirmadas = exatamente a contagem LoTW (fonte única de verdade). O número oficial
  // do LoTW (lotwDxcc) é mais preciso que a contagem por entidade da matriz, então igualamos.
  var dxccConfirmedCount = dxccLotwConfirmedCount;
  var wacWorked = 0; var wacConfirmed = 0;
  for (var wk in wacData) { if (wacData[wk].worked) wacWorked++; if (wacData[wk].confirmed) wacConfirmed++; }
  var wazWorked = 0; var wazConfirmed = 0;
  for (var zk in wazData) { if (wazData[zk].worked) wazWorked++; if (wazData[zk].confirmed) wazConfirmed++; }

  var sortObj = function(o) {
    return Object.entries(o).sort(function(a, b) { return b[1] - a[1]; });
  };
  var validD = dists.filter(function(d) { return d > 0; });
  var hoursSorted = sortObj(byHour);
  var bandsSorted = sortObj(byBand);

  // Hourly data — AVERAGE per operating day, not sum
  var hourlyData = [];
  for (var h = 0; h < 24; h++) {
    var hStr = String(h).padStart(2, "0");
    var totalH = byHour[hStr] || 0;
    var daysH = daysPerHour[hStr] ? daysPerHour[hStr].size : 0;
    var avgH = daysH > 0 ? parseFloat((totalH / daysH).toFixed(1)) : 0;
    hourlyData.push({ hour: hStr + "h", qsos: avgH, total: totalH, days: daysH });
  }

  // Date data
  var dateEntries = Object.entries(byDate)
    .filter(function(e) { return e[0] !== "?"; })
    .sort(function(a, b) { return a[0].localeCompare(b[0]); });
  var dateData = dateEntries.map(function(e) {
    return { date: e[0].replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3"), qsos: e[1] };
  });

  // Log period
  var validDates = Object.keys(byDate).filter(function(d) { return d !== "?" && d.length === 8; }).sort();
  var logDays = 1, firstDate = "", lastDate = "";
  if (validDates.length >= 2) {
    firstDate = validDates[0]; lastDate = validDates[validDates.length - 1];
    var y1 = parseInt(firstDate.substring(0,4)), m1 = parseInt(firstDate.substring(4,6))-1, d1 = parseInt(firstDate.substring(6,8));
    var y2 = parseInt(lastDate.substring(0,4)), m2 = parseInt(lastDate.substring(4,6))-1, d2 = parseInt(lastDate.substring(6,8));
    logDays = Math.max(1, Math.round((new Date(y2,m2,d2).getTime() - new Date(y1,m1,d1).getTime()) / 86400000) + 1);
  } else if (validDates.length === 1) { firstDate = validDates[0]; lastDate = validDates[0]; }
  var totalHoursInPeriod = logDays * 24;

  // Distances
  var maxD = 0, minD = 0, avgD = 0;
  if (validD.length > 0) {
    // Um valor por QSO: com log grande esse array passa de 100 mil itens, e
    // Math.max.apply estoura a pilha (RangeError) — derrubava o dashboard
    // inteiro, sem mensagem, porque o .catch do fetch engolia o erro.
    maxD = Math.round(maxOf(validD));
    minD = Math.round(minOf(validD));
    avgD = Math.round(validD.reduce(function(a, b) { return a + b; }, 0) / validD.length);
  }
  var uniqueCountries = countries.size;
  if (countries.has("Unknown")) uniqueCountries--;

  // Hourly by band
  var bandOrder = ["160m","80m","60m","40m","30m","20m","17m","15m","12m","10m","6m","2m","70cm"];
  var allBands = Object.keys(byBand).sort(function(a, b) {
    var ia = bandOrder.indexOf(a), ib = bandOrder.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  var hourlyByBand = [];
  for (var hi = 0; hi < 24; hi++) {
    var hbStr = String(hi).padStart(2, "0");
    var row = { hour: hbStr + "h" };
    for (var bi = 0; bi < allBands.length; bi++) row[allBands[bi]] = 0;
    hourlyByBand.push(row);
  }
  // Track days per hour+band for averaging
  var daysPerHourBand = {}; // key: "HH:band" -> Set of dates
  for (var qi = 0; qi < qsos.length; qi++) {
    var qq = qsos[qi];
    var qHourRaw = qq.time ? parseInt(qq.time.substring(0, 2)) : -1;
    var qHour = qHourRaw >= 0 ? adjustHour(qHourRaw, utcOffset) : -1;
    var qBand = qq.band || "?";
    var qDate = qq.date || "?";
    if (qHour >= 0 && qHour < 24 && hourlyByBand[qHour][qBand] !== undefined) {
      hourlyByBand[qHour][qBand]++;
      // Track unique days for this hour+band
      var hbKey = String(qHour).padStart(2, "0") + ":" + qBand;
      if (!daysPerHourBand[hbKey]) daysPerHourBand[hbKey] = new Set();
      if (qDate !== "?") daysPerHourBand[hbKey].add(qDate);
    }
  }
  // Convert hourlyByBand sums to averages
  for (var ahi = 0; ahi < 24; ahi++) {
    var ahStr = String(ahi).padStart(2, "0");
    for (var abi = 0; abi < allBands.length; abi++) {
      var abKey = ahStr + ":" + allBands[abi];
      var abDays = daysPerHourBand[abKey] ? daysPerHourBand[abKey].size : 0;
      if (abDays > 0) {
        hourlyByBand[ahi][allBands[abi]] = parseFloat((hourlyByBand[ahi][allBands[abi]] / abDays).toFixed(1));
      }
    }
  }

  // ── IMPROVED Day/Night band analysis ────────────────────────
  // Instead of just counting QSOs, we now:
  // 1. Track which hours the operator was ACTUALLY active
  // 2. Filter out bands that contradict propagation physics
  // 3. Only recommend with sufficient data
  var activeHours = getActiveHours(qsos, utcOffset);
  var dayHoursActive = 0, nightHoursActive = 0;
  activeHours.forEach(function(h) {
    if (h >= 9 && h < 21) dayHoursActive++; else nightHoursActive++;
  });

  var dayBands = {}, nightBands = {};
  var dayBandHours = {}, nightBandHours = {}; // track unique active hours per band
  for (var dni = 0; dni < qsos.length; dni++) {
    var dnq = qsos[dni];
    var dnHourRaw = dnq.time ? parseInt(dnq.time.substring(0, 2)) : -1;
    var dnHour = dnHourRaw >= 0 ? adjustHour(dnHourRaw, utcOffset) : -1;
    var dnBand = dnq.band || "?";
    if (dnHour >= 0) {
      if (dnHour >= 9 && dnHour < 21) {
        dayBands[dnBand] = (dayBands[dnBand] || 0) + 1;
        if (!dayBandHours[dnBand]) dayBandHours[dnBand] = new Set();
        dayBandHours[dnBand].add(dnHour);
      } else {
        nightBands[dnBand] = (nightBands[dnBand] || 0) + 1;
        if (!nightBandHours[dnBand]) nightBandHours[dnBand] = new Set();
        nightBandHours[dnBand].add(dnHour);
      }
    }
  }

  // Score bands considering propagation consistency and sample size
  function scoreBands(bandCounts, bandHours, period) {
    return Object.entries(bandCounts)
      .filter(function(e) { return e[1] >= MIN_QSOS_FOR_RECOMMENDATION; })
      .map(function(e) {
        var band = e[0], count = e[1];
        var hoursSpread = bandHours[band] ? bandHours[band].size : 1;
        var consistent = isPropagationConsistent(band, period);
        var prop = BAND_PROPAGATION[band];
        var propWeight = prop ? (period === "day" ? prop.dayWeight : prop.nightWeight) : 0.5;
        // Score = QSOs * propagation_weight * hours_spread_bonus
        // Bands that contradict physics get heavily penalized
        var score = count * propWeight * (1 + Math.log2(hoursSpread));
        return { band: band, count: count, score: score, hours: hoursSpread, consistent: consistent, confidence: confidence(count) };
      })
      .sort(function(a, b) { return b.score - a.score; });
  }

  var dayScored = scoreBands(dayBands, dayBandHours, "day");
  var nightScored = scoreBands(nightBands, nightBandHours, "night");

  var topBandDay = dayScored[0] ? dayScored[0].band : "N/A";
  var topBandDayQsos = dayScored[0] ? dayScored[0].count : 0;
  var topBandDayConf = dayScored[0] ? dayScored[0].confidence : "insuficiente";
  var topBandDayConsistent = dayScored[0] ? dayScored[0].consistent : false;

  var topBandNight = nightScored[0] ? nightScored[0].band : "N/A";
  var topBandNightQsos = nightScored[0] ? nightScored[0].count : 0;
  var topBandNightConf = nightScored[0] ? nightScored[0].confidence : "insuficiente";
  var topBandNightConsistent = nightScored[0] ? nightScored[0].consistent : false;

  return {
    total: qsos.length,
    uniqueCalls: calls.size,
    uniqueGrids: grids.size,
    uniqueCountries: uniqueCountries,
    byBand: bandsSorted,
    byMode: sortObj(byMode),
    byCountry: sortObj(byCountry),
    byCont: sortObj(byCont),
    byHour: hoursSorted,
    byDate: sortObj(byDate),
    byCQ: sortObj(byCQ),
    bestHour: hoursSorted[0] ? hoursSorted[0][0] + "h " + tzStr : "N/A",
    bestHourQsos: hoursSorted[0] ? hoursSorted[0][1] : 0,
    bestBand: bandsSorted[0] ? bandsSorted[0][0] : "N/A",
    bestBandQsos: bandsSorted[0] ? bandsSorted[0][1] : 0,
    maxDist: maxD, minDist: minD, avgDist: avgD,
    avgQsoPerHour: totalHoursInPeriod ? parseFloat((qsos.length / totalHoursInPeriod).toFixed(1)) : 0,
    logDays: logDays, firstDate: firstDate, lastDate: lastDate,
    hourlyData: hourlyData, dateData: dateData,
    myLat: mc.lat, myLon: mc.lon,
    bandChartData: bandsSorted.map(function(e) { return { name: e[0], qsos: e[1] }; }),
    modeChartData: sortObj(byMode).map(function(e) { return { name: e[0], qsos: e[1] }; }),
    contChartData: sortObj(byCont).filter(function(e) { return e[0] !== "??"; }).map(function(e) { return { name: e[0], qsos: e[1] }; }),
    countryChartData: sortObj(byCountry).slice(0, 20).map(function(e) {
      return { name: e[0].length > 20 ? e[0].substring(0, 18) + ".." : e[0], qsos: e[1], full: e[0] };
    }),
    withCoords: qsos.filter(function(q) { return q.lat != null; }).length,
    hourlyByBand: hourlyByBand, allBands: allBands,
    // Day/Night - improved with propagation awareness
    topBandDay: topBandDay,
    topBandDayQsos: topBandDayQsos,
    topBandDayConf: topBandDayConf,
    topBandDayConsistent: topBandDayConsistent,
    topBandNight: topBandNight,
    topBandNightQsos: topBandNightQsos,
    topBandNightConf: topBandNightConf,
    topBandNightConsistent: topBandNightConsistent,
    dayBandsSorted: dayScored.map(function(s) { return [s.band, s.count]; }),
    nightBandsSorted: nightScored.map(function(s) { return [s.band, s.count]; }),
    dayHoursActive: dayHoursActive,
    nightHoursActive: nightHoursActive,
    utcOffset: utcOffset,
    tzStr: tzStr,
    // DXCC Tracker
    dxccData: dxccData,
    dxccWorkedCount: dxccWorkedCount,
    dxccConfirmedCount: dxccConfirmedCount,
    dxccLotwConfirmedCount: dxccLotwConfirmedCount,
    // WAC / WAZ
    wacData: wacData, wacWorked: wacWorked, wacConfirmed: wacConfirmed,
    wazData: wazData, wazWorked: wazWorked, wazConfirmed: wazConfirmed
  };
}

// ════════════════════════════════════════════════════════════════
// computeDXIntelligence — REWRITTEN with propagation awareness
// ════════════════════════════════════════════════════════════════
export function computeDXIntelligence(qsos, stats) {
  var utcOffset = stats && stats.utcOffset !== undefined ? stats.utcOffset : 0;
  var tzStr = stats && stats.tzStr ? stats.tzStr : "UTC";
  if (!qsos || !qsos.length) return null;

  // ── Best band per 3h slot (unchanged logic) ─────────────────
  var slots = [];
  for (var s = 0; s < 8; s++) {
    var startH = s * 3, endH = startH + 3;
    var slotLabel = String(startH).padStart(2, "0") + "-" + String(endH).padStart(2, "0") + " " + tzStr;
    var bandCount = {}, slotTotal = 0;
    var slotActiveHours = new Set();
    for (var qi = 0; qi < qsos.length; qi++) {
      var q = qsos[qi];
      var hRaw = q.time ? parseInt(q.time.substring(0, 2)) : -1;
      var h = hRaw >= 0 ? adjustHour(hRaw, utcOffset) : -1;
      if (h >= startH && h < endH) {
        var b = q.band || "?";
        bandCount[b] = (bandCount[b] || 0) + 1;
        slotTotal++;
        slotActiveHours.add(h);
      }
    }
    var sorted = Object.entries(bandCount).sort(function(a, b) { return b[1] - a[1]; });
    var isDaySlot = startH >= 9 && startH < 21;
    var period = isDaySlot ? "day" : "night";
    // Flag if top band contradicts propagation for this time
    var topBandPropOk = sorted[0] ? isPropagationConsistent(sorted[0][0], period) : true;
    slots.push({
      label: slotLabel, start: startH, end: endH, total: slotTotal,
      topBand: sorted[0] ? sorted[0][0] : "-",
      topBandQsos: sorted[0] ? sorted[0][1] : 0,
      secondBand: sorted[1] ? sorted[1][0] : "-",
      secondBandQsos: sorted[1] ? sorted[1][1] : 0,
      allBands: sorted.slice(0, 5),
      activeHours: slotActiveHours.size,
      confidence: confidence(slotTotal),
      propConsistent: topBandPropOk,
      period: period
    });
  }

  // ── Best continent per band (unchanged) ─────────────────────
  var bandContinent = {};
  for (var qi2 = 0; qi2 < qsos.length; qi2++) {
    var q2 = qsos[qi2];
    var band = q2.band || "?";
    var cont = q2.continent || "??";
    if (cont === "??") continue;
    if (!bandContinent[band]) bandContinent[band] = {};
    bandContinent[band][cont] = (bandContinent[band][cont] || 0) + 1;
  }
  var bandContinentTop = {};
  Object.keys(bandContinent).forEach(function(band) {
    var sorted2 = Object.entries(bandContinent[band]).sort(function(a, b) { return b[1] - a[1]; });
    bandContinentTop[band] = sorted2.slice(0, 3);
  });

  // ── Peak performance window ─────────────────────────────────
  var bestSlot = slots[0];
  for (var si = 1; si < slots.length; si++) {
    if (slots[si].total > bestSlot.total) bestSlot = slots[si];
  }

  // ── DX Score per band (weighted by distance) ────────────────
  var bandDxScore = {}, bandDxCount = {};
  for (var qi3 = 0; qi3 < qsos.length; qi3++) {
    var q3 = qsos[qi3];
    if (q3.distance && q3.distance > 0) {
      var b3 = q3.band || "?";
      bandDxScore[b3] = (bandDxScore[b3] || 0) + q3.distance;
      bandDxCount[b3] = (bandDxCount[b3] || 0) + 1;
    }
  }
  var bandAvgDist = [];
  Object.keys(bandDxScore).forEach(function(band) {
    bandAvgDist.push({ band: band, avgDist: Math.round(bandDxScore[band] / bandDxCount[band]), qsos: bandDxCount[band] });
  });
  bandAvgDist.sort(function(a, b) { return b.avgDist - a.avgDist; });

  // ── SMART Recommendations ───────────────────────────────────
  // Now considers: propagation physics, sample size, active hours,
  // and warns when data is insufficient
  var daySlots = slots.filter(function(s) { return s.start >= 9 && s.start < 21; });
  var nightSlots = slots.filter(function(s) { return s.start >= 21 || s.start < 9; });

  var bestDay = null;
  daySlots.forEach(function(s) { if (s.total > 0 && (!bestDay || s.total > bestDay.total)) bestDay = s; });
  var bestNight = null;
  nightSlots.forEach(function(s) { if (s.total > 0 && (!bestNight || s.total > bestNight.total)) bestNight = s; });

  var recs = [];

  // Day recommendation
  if (bestDay && bestDay.total >= MIN_QSOS_FOR_RECOMMENDATION) {
    var dayConf = confidence(bestDay.total);
    recs.push({
      type: "day", confidence: dayConf, icon: "☀️",
      text: "Melhor janela diurna (" + tzStr + "): " + bestDay.label +
        " (" + bestDay.topBand + " com " + bestDay.topBandQsos + " QSOs). " +
        "Confiança: " + dayConf + " (" + bestDay.total + " QSOs, " + bestDay.activeHours + "h ativas)."
    });
  } else if (bestDay) {
    recs.push({
      type: "day", confidence: "insuficiente", icon: "⚠️",
      text: "Dados diurnos insuficientes (apenas " + bestDay.total +
        " QSOs no slot " + bestDay.label + "). Opere mais de dia para recomendações confiáveis."
    });
  } else {
    recs.push({ type: "day", confidence: "insuficiente", icon: "⚠️",
      text: "Nenhum QSO registrado no período diurno (09-21 " + tzStr + "). Sem dados para recomendar." });
  }

  // Night recommendation
  if (bestNight && bestNight.total >= MIN_QSOS_FOR_RECOMMENDATION) {
    var nightConf = confidence(bestNight.total);
    // Check if the recommended band makes propagation sense at night
    if (!bestNight.propConsistent) {
      var prop = BAND_PROPAGATION[bestNight.topBand];
      var propType = prop ? prop.type : "unknown";
      recs.push({
        type: "night", confidence: "baixa", icon: "🔬",
        text: "Slot noturno " + bestNight.label + " tem mais QSOs em " + bestNight.topBand +
          " (" + bestNight.topBandQsos + "), mas essa é uma banda " + (propType === "day" ? "DIURNA" : propType) +
          " — provavelmente QSOs de fim de tarde/início de noite, não propagação noturna real. " +
          "Bandas noturnas típicas: 40m, 80m, 160m."
      });
      // Find the best propagation-consistent night band
      var consistentNight = null;
      for (var ni = 0; ni < nightSlots.length; ni++) {
        var ns = nightSlots[ni];
        if (ns.total >= MIN_QSOS_FOR_RECOMMENDATION && isPropagationConsistent(ns.topBand, "night")) {
          if (!consistentNight || ns.total > consistentNight.total) consistentNight = ns;
        }
      }
      if (consistentNight) {
        recs.push({
          type: "night_alt", confidence: confidence(consistentNight.total), icon: "🌙",
          text: "Melhor janela noturna real: " + consistentNight.label +
            " (" + consistentNight.topBand + " com " + consistentNight.topBandQsos + " QSOs). " +
            "Esta banda é consistente com propagação noturna."
        });
      }
    } else {
      recs.push({
        type: "night", confidence: nightConf, icon: "🌙",
        text: "Melhor janela noturna (" + tzStr + "): " + bestNight.label +
          " (" + bestNight.topBand + " com " + bestNight.topBandQsos + " QSOs). " +
          "Confiança: " + nightConf + " (" + bestNight.total + " QSOs, " + bestNight.activeHours + "h ativas)."
      });
    }
  } else if (bestNight) {
    recs.push({
      type: "night", confidence: "insuficiente", icon: "⚠️",
      text: "Dados noturnos insuficientes (apenas " + bestNight.total +
        " QSOs no slot " + bestNight.label + "). Opere mais à noite para recomendações confiáveis."
    });
  } else {
    recs.push({ type: "night", confidence: "insuficiente", icon: "⚠️",
      text: "Nenhum QSO registrado no período noturno (21-09 " + tzStr + "). Sem dados para recomendar." });
  }

  // DX distance recommendation
  if (bandAvgDist[0] && bandAvgDist[0].qsos >= MIN_QSOS_FOR_RECOMMENDATION) {
    recs.push({
      type: "dx", confidence: confidence(bandAvgDist[0].qsos), icon: "📡",
      text: "Banda " + bandAvgDist[0].band + " tem a maior distância média (" +
        bandAvgDist[0].avgDist.toLocaleString() + " km, " + bandAvgDist[0].qsos + " QSOs). Ideal para DX longo."
    });
  }

  // Strategy recommendation (only if both day/night have enough data)
  if (stats.topBandDay !== "N/A" && stats.topBandNight !== "N/A" &&
      stats.topBandDay !== stats.topBandNight &&
      stats.topBandDayQsos >= MIN_QSOS_FOR_RECOMMENDATION &&
      stats.topBandNightQsos >= MIN_QSOS_FOR_RECOMMENDATION) {
    // Only recommend the strategy if the night band is propagation-consistent
    if (stats.topBandNightConsistent) {
      recs.push({
        type: "strategy", confidence: "media", icon: "🎯",
        text: "Estratégia: de dia use " + stats.topBandDay + ", de noite mude para " + stats.topBandNight + "."
      });
    }
  }

  // Coverage gaps warning
  if (stats.nightHoursActive < 2 && stats.dayHoursActive > 3) {
    recs.push({
      type: "coverage", confidence: "info", icon: "📊",
      text: "Seu log tem pouca atividade noturna (" + stats.nightHoursActive +
        " horas). As recomendações noturnas podem não refletir propagação real. " +
        "Experimente operar 40m/80m entre 21-03h " + tzStr + " para descobrir novas janelas."
    });
  }
  if (stats.dayHoursActive < 2 && stats.nightHoursActive > 3) {
    recs.push({
      type: "coverage", confidence: "info", icon: "📊",
      text: "Seu log tem pouca atividade diurna (" + stats.dayHoursActive +
        " horas). Experimente 15m/10m entre 10-16h " + tzStr + " — são bandas de longo alcance durante o dia."
    });
  }

  // Build backward-compatible recData (plain text array) for existing UI
  var recData = recs.map(function(r) { return r.text; });

  return {
    slots: slots,
    bestSlot: bestSlot,
    bandContinentTop: bandContinentTop,
    bandAvgDist: bandAvgDist,
    recData: recData,
    // New structured recommendations with metadata
    recommendations: recs
  };
}
